import { useState, useRef, ChangeEventHandler } from "react";
import { Dropdown, Form, Button } from "react-bootstrap";
import messages from "messages";
// components
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import { ReactComponent as OutlineFolderCreate } from "assets/svg/outline-folder-create.svg";
import { ReactComponent as OutlineFolderUpload } from "assets/svg/outline-folder-upload.svg";
import { ReactComponent as OutlineDocumentUpload } from "assets/svg/outline-document-upload.svg";

import IconButton from "components/icon-button";
// styles
import styles from "./styles.module.scss";
// types
import type { CreateDirectoryFunc } from "contexts/orchid-drive/components/orchidDriveProvider";
import { AmplitudePageName, track } from "utils/amplitude";

type Props = {
  storageDrive: StorageDrive;
  currDirId: string | null;
  patientUserId?: string;
  createDirectory: CreateDirectoryFunc;
  onUpload: ChangeEventHandler<HTMLInputElement>;
  analyticsPageName: AmplitudePageName;
};

const UploadMenu = ({
  patientUserId,
  createDirectory,
  onUpload,
  currDirId,
  storageDrive,
  analyticsPageName,
}: Props) => {
  const [showDirNameModal, setShowDirNameModal] = useState(false);
  const [folderName, setFolderName] = useState("");
  const inputFile = useRef<HTMLInputElement | null>(null);
  const directoryFile = useRef<HTMLInputElement | null>(null);

  const createFolder = (e) => {
    e.preventDefault();
    createDirectory({
      storageDrive,
      patientUserId: patientUserId,
      name: folderName,
      parentDirectoryId: currDirId,
    });
    setShowDirNameModal(false);
  };

  return (
    <>
      <ModalLayout
        size={"md"}
        show={showDirNameModal}
        onHide={() => {
          track(
            analyticsPageName,
            ["new-folder-modal", "close-button"],
            "click",
          );
          setShowDirNameModal(false);
        }}
        contentClassName={styles["folder-name-modal__content"]}
        Header={"New Folder"}
        headerClass={styles["folder-name-modal__header"]}
        bodyClass={styles["folder-name-modal__body"]}
        Footer={
          <Button
            className={styles["folder-name-modal__apply"]}
            variant="primary"
            type="submit"
            form="folder-name"
            onClick={() => {
              track(
                analyticsPageName,
                ["new-folder-modal", "create-button"],
                "click",
              );
            }}
          >
            {messages.create}
          </Button>
        }
        footerClass={styles["folder-name-modal__footer"]}
      >
        <Form id="folder-name" onSubmit={createFolder}>
          <Form.Group controlId="folderName">
            <Form.Control
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder={"Untitled Folder"}
              onFocus={() => {
                track(
                  analyticsPageName,
                  ["new-folder-modal", "folder-name-field"],
                  "focus",
                );
              }}
            />
          </Form.Group>
        </Form>
      </ModalLayout>
      <Dropdown
        className={styles["upload-menu"]}
        onMouseDown={(e) => e.stopPropagation()}
        drop={"end"}
      >
        <Dropdown.Toggle
          bsPrefix="p-0"
          as={IconButton}
          className={styles["upload-menu__toggle"]}
        >
          <OutlinePlus />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <input
            type="file"
            id="singleFileInput"
            multiple
            ref={inputFile}
            onChange={onUpload}
            style={{ display: "none" }}
          />
          <input
            type="file"
            id="directoryInput"
            multiple
            ref={directoryFile}
            onChange={onUpload}
            style={{ display: "none" }}
            /**
             * restrict safari to upload files instead of folder
             * @see https://stackoverflow.com/questions/55649945/folder-upload-using-webkitdirectory-and-directory-not-working-on-safari-browser
             */
            accept={".."}
          />
          <Dropdown.Item
            draggable={"false"}
            className={styles["upload-menu__action-item"]}
            onClick={() => {
              track(analyticsPageName, "create-folder-dropdown-item", "click");
              setFolderName("");
              setShowDirNameModal(true);
            }}
          >
            <OutlineFolderCreate className="mr-2" />
            {messages.createFolder}
          </Dropdown.Item>
          <Dropdown.Item
            draggable={"false"}
            className={styles["upload-menu__action-item"]}
            onClick={() => {
              track(analyticsPageName, "upload-file-dropdown-item", "click");
              inputFile!.current!.click();
            }}
          >
            <OutlineDocumentUpload className="mr-2" />
            {messages.fileUpload}
          </Dropdown.Item>
          <Dropdown.Item
            draggable={"false"}
            className={styles["upload-menu__action-item"]}
            onClick={() => {
              track(analyticsPageName, "upload-folder-dropdown-item", "click");
              directoryFile!.current!.setAttribute("directory", "");
              directoryFile!.current!.setAttribute("webkitdirectory", "");
              directoryFile!.current!.click();
            }}
          >
            <OutlineFolderUpload className="mr-2" />
            {messages.folderUpload}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export default UploadMenu;
